import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Printer, Usb, Wifi, CreditCard, Tag, Receipt, RefreshCw, ShieldCheck, ShieldAlert, PlayCircle, Zap, Lock, Eye, EyeOff } from "lucide-react";
import { CLUB_TIERS } from "@/lib/constants";
import CashierPinCard from "@/components/CashierPinCard";

// Kompakt-Field für Preise/Zahlen — außerhalb Settings damit React nicht neu mountet
function PriceField({ label, k, step = 0.5, value, onChange }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-1">{label}</div>
      <input type="number" step={step} value={value ?? ""} onChange={(e) => onChange(parseFloat(e.target.value || 0))}
        className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-sm focus:border-[#D4AF37] outline-none" />
    </label>
  );
}

const DAYS = [{ v: 0, l: "Mo" }, { v: 1, l: "Di" }, { v: 2, l: "Mi" }, { v: 3, l: "Do" }, { v: 4, l: "Fr" }, { v: 5, l: "Sa" }, { v: 6, l: "So" }];
const PRESETS = [
  { label: "Wochenend-Peak", days: [4, 5], from_time: "19:00", to_time: "23:59", surcharge: 3 },
  { label: "Ferien-Matinée", days: [0, 1, 2, 3], from_time: "10:00", to_time: "14:00", surcharge: -1 },
  { label: "Silvester-Zuschlag", days: [4], from_time: "20:00", to_time: "23:59", surcharge: 5 },
];

function DynamicPricingCard({ s, upd }) {
  const rules = s.dynamic_pricing_rules || [];
  const setRules = (rs) => upd("dynamic_pricing_rules")(rs);
  const patch = (i, k, v) => setRules(rules.map((r, ii) => ii === i ? { ...r, [k]: v } : r));
  const toggleDay = (i, d) => {
    const cur = rules[i].days || [];
    const next = cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort();
    patch(i, "days", next);
  };
  const remove = (i) => setRules(rules.filter((_, ii) => ii !== i));
  const add = (preset) => setRules([...rules, preset ? { ...preset } : { label: "Neue Regel", days: [4, 5], from_time: "19:00", to_time: "23:59", surcharge: 2 }]);
  const [preview, setPreview] = React.useState(null);
  const runPreview = async () => {
    try {
      const now = new Date();
      const d = now.toISOString().slice(0, 10);
      const t = now.toTimeString().slice(0, 5);
      const { data } = await api.get(`/dynamic-pricing/preview?base_price=${s.ticket_price || 8}&date=${d}&time=${t}`);
      setPreview(data);
    } catch { /* noop */ }
  };
  return (
    <div className="card-lux p-6" data-testid="settings-dynamic-pricing">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Dynamic Pricing</div>
          <div className="font-heading text-2xl">Preis-Regeln nach Zeit</div>
          <div className="text-[10px] text-[#575148] mt-1">Aufpreise oder Rabatte für bestimmte Wochentage & Zeitfenster. Werden auf den Basis-Ticketpreis angewendet.</div>
        </div>
        <div className="flex gap-2">
          {PRESETS.map((p) => (
            <button key={p.label} onClick={() => add(p)} data-testid={`dyn-preset-${p.label.replace(/\W+/g,'-').toLowerCase()}`}
              className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#2A2A2A] text-[#A8A398] rounded hover:border-[#D4AF37] hover:text-[#D4AF37]">
              + {p.label}
            </button>
          ))}
          <button onClick={() => add(null)} data-testid="dyn-add" className="text-[10px] uppercase tracking-widest px-2 py-1 border border-[#D4AF37] text-[#D4AF37] rounded hover:bg-[#D4AF37]/10">
            + Leere Regel
          </button>
        </div>
      </div>
      {rules.length === 0 && <div className="text-center text-[#575148] text-sm py-6">Noch keine Regeln. Wähle ein Preset oder erstell eine leere Regel.</div>}
      <div className="space-y-2">
        {rules.map((r, i) => (
          <div key={i} className="border border-[#1A1A1A] bg-[#050505] rounded p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 items-center">
            <input value={r.label || ""} onChange={(e) => patch(i, "label", e.target.value)} placeholder="Label (z. B. Freitagabend)"
              data-testid={`dyn-label-${i}`} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-sm outline-none focus:border-[#D4AF37]" />
            <div className="flex gap-1">
              {DAYS.map((d) => (
                <button key={d.v} onClick={() => toggleDay(i, d.v)} data-testid={`dyn-day-${i}-${d.v}`}
                  className={`w-8 h-8 rounded text-[10px] font-mono uppercase transitions-safe ${(r.days || []).includes(d.v) ? "bg-[#D4AF37] text-black" : "bg-[#0A0A0A] text-[#575148] border border-[#1A1A1A] hover:border-[#D4AF37]"}`}>
                  {d.l}
                </button>
              ))}
            </div>
            <div className="flex gap-1 items-center">
              <input type="time" value={r.from_time || "19:00"} onChange={(e) => patch(i, "from_time", e.target.value)}
                className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
              <span className="text-[#575148] text-xs">–</span>
              <input type="time" value={r.to_time || "23:59"} onChange={(e) => patch(i, "to_time", e.target.value)}
                className="bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="flex items-center gap-1">
              <input type="number" step="0.5" value={r.surcharge ?? 0} onChange={(e) => patch(i, "surcharge", parseFloat(e.target.value || 0))}
                data-testid={`dyn-surcharge-${i}`} className="w-20 bg-[#0A0A0A] border border-[#1A1A1A] rounded px-2 py-1 text-xs font-mono outline-none focus:border-[#D4AF37]" />
              <span className="text-[10px] text-[#A8A398]">€</span>
            </div>
            <button onClick={() => remove(i)} className="text-[#EE9CA1] p-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      {rules.length > 0 && (
        <div className="mt-4 flex items-center gap-3">
          <button onClick={runPreview} className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-[#8FBC8F]/40 text-[#8FBC8F] rounded hover:bg-[#8FBC8F]/10" data-testid="dyn-preview">Live-Preview jetzt</button>
          {preview && (
            <div className="text-xs text-[#A8A398]">
              Basis <b className="text-[#EEDC82]">{preview.base?.toFixed(2)} €</b> · aktuell <b className="text-[#D4AF37]">{preview.final?.toFixed(2)} €</b>
              {preview.surcharge !== 0 && <> ({preview.surcharge > 0 ? "+" : ""}{preview.surcharge.toFixed(2)} €)</>}
              {preview.label && <> · <span className="text-[#EEDC82]">{preview.label}</span></>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LandingPinCard({ s, upd, save }) {
  const [show, setShow] = React.useState(false);
  const [val, setVal] = React.useState(s.landing_pin || "");
  React.useEffect(() => { setVal(s.landing_pin || ""); }, [s.landing_pin]);
  const active = !!(s.landing_pin || "").trim();
  const onSave = async () => {
    upd("landing_pin")(val.trim());
    // trigger save after state update — kleine Verzögerung, um Setter flushen zu lassen
    setTimeout(() => save(), 50);
  };
  const onClear = async () => {
    setVal("");
    upd("landing_pin")("");
    setTimeout(() => save(), 50);
  };
  return (
    <div className="card-lux p-6" data-testid="settings-landing-pin">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${active ? "bg-[#D4AF37]/15 text-[#D4AF37]" : "bg-[#1A1A1A] text-[#575148]"}`}>
            <Lock size={18} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Privates Kino</div>
            <div className="font-heading text-2xl">Landing-PIN</div>
            <div className="text-[11px] text-[#A8A398] mt-1 max-w-xl">
              Sperrt die öffentliche Startseite hinter einem Code. Nur Freunde mit dem PIN erreichen das Kino.
              Der PIN wird pro Browser dauerhaft gespeichert (bis er hier geändert wird). Feld leer lassen = Landing öffentlich.
            </div>
          </div>
        </div>
        <div className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded ${active ? "bg-[#8FBC8F]/15 text-[#8FBC8F]" : "bg-[#575148]/15 text-[#A8A398]"}`}>
          {active ? "Aktiv · Landing geschützt" : "Aus · Landing öffentlich"}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-2 items-center">
        <div className="relative">
          <input
            type={show ? "text" : "password"}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            placeholder="PIN (z.B. 4711 oder ein Wort)"
            autoComplete="new-password"
            data-testid="landing-pin-input"
            className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 font-mono text-lg tracking-widest outline-none focus:border-[#D4AF37] pr-10"
          />
          <button type="button" onClick={() => setShow((x) => !x)} data-testid="landing-pin-toggle"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[#575148] hover:text-[#D4AF37] p-1">
            {show ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <button onClick={onSave} data-testid="landing-pin-save"
          className="px-4 py-2 bg-[#D4AF37] text-black rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82] disabled:opacity-40"
          disabled={!val.trim()}>
          PIN speichern
        </button>
        {active && (
          <button onClick={onClear} data-testid="landing-pin-clear"
            className="px-4 py-2 border border-[#EE9CA1]/40 text-[#EE9CA1] rounded text-[11px] uppercase tracking-widest hover:bg-[#EE9CA1]/10">
            Deaktivieren
          </button>
        )}
      </div>
      <div className="text-[10px] text-[#575148] mt-3">
        Hinweis: Nach dem Speichern werden alle Besucher (auch du selbst) beim nächsten Öffnen der Landing nach dem PIN gefragt. Der Admin-Bereich bleibt unabhängig davon per Login erreichbar.
      </div>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const upd = (k) => (v) => setS((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="text-[#A8A398]">Lade …</div>;

  const save = async () => {
    try {
      await api.put("/settings", s);
      toast.success("Gespeichert");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const updBenefit = (tier, i, val) => {
    setS((x) => {
      const tb = { ...(x.tier_benefits || {}) };
      const arr = [...(tb[tier] || [])];
      arr[i] = val;
      tb[tier] = arr;
      return { ...x, tier_benefits: tb };
    });
  };
  const addBenefit = (tier) => setS((x) => {
    const tb = { ...(x.tier_benefits || {}) };
    tb[tier] = [...(tb[tier] || []), ""];
    return { ...x, tier_benefits: tb };
  });
  const removeBenefit = (tier, i) => setS((x) => {
    const tb = { ...(x.tier_benefits || {}) };
    tb[tier] = (tb[tier] || []).filter((_, idx) => idx !== i);
    return { ...x, tier_benefits: tb };
  });

  // ---- Landing-Page-Inhalte ----
  const promo = s.promo || { active: false, title: "", text: "", cta: "", cta_href: "" };
  const updPromo = (k) => (v) => setS((x) => ({ ...x, promo: { ...(x.promo || {}), [k]: v } }));

  const testimonials = s.testimonials || [];
  const updTest = (i, k, v) => setS((x) => {
    const arr = [...(x.testimonials || [])];
    arr[i] = { ...(arr[i] || {}), [k]: v };
    return { ...x, testimonials: arr };
  });
  const addTest = () => setS((x) => ({ ...x, testimonials: [...(x.testimonials || []), { author: "", text: "" }] }));
  const removeTest = (i) => setS((x) => ({ ...x, testimonials: (x.testimonials || []).filter((_, idx) => idx !== i) }));

  const faq = s.faq || [];
  const updFaq = (i, k, v) => setS((x) => {
    const arr = [...(x.faq || [])];
    arr[i] = { ...(arr[i] || {}), [k]: v };
    return { ...x, faq: arr };
  });
  const addFaq = () => setS((x) => ({ ...x, faq: [...(x.faq || []), { q: "", a: "" }] }));
  const removeFaq = (i) => setS((x) => ({ ...x, faq: (x.faq || []).filter((_, idx) => idx !== i) }));

  const voucherAmounts = (s.voucher_amounts || []).join(", ");

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Konfiguration</div>
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl">Einstellungen</h1>
      </div>

      <CashierPinCard />

      <LandingPinCard s={s} upd={upd} save={save} />

      <div className="card-lux p-6 grid md:grid-cols-2 gap-4">
        <F label="Kinoname" v={s.cinema_name} on={upd("cinema_name")} />
        <F label="Slogan" v={s.slogan} on={upd("slogan")} />
        <F label="Logo URL" v={s.logo_url} on={upd("logo_url")} />
        <F label="Adresse" v={s.address} on={upd("address")} />
        <F label="Währung" v={s.currency} on={upd("currency")} />
        <F label="Zeitzone" v={s.timezone} on={upd("timezone")} />
        <F label="Standardsprache" v={s.default_language} on={upd("default_language")} />
        <F label="Ticketpräfix" v={s.ticket_prefix} on={upd("ticket_prefix")} />
        <F label="Reservierungsdauer (Min)" v={s.reservation_hold_minutes} on={(v) => upd("reservation_hold_minutes")(parseInt(v||0))} type="number" />
        <F label="Steuersatz" v={s.tax_rate} on={(v) => upd("tax_rate")(parseFloat(v||0))} type="number" />
        <F label="E-Mail-Absender" v={s.email_from} on={upd("email_from")} />
        <F label="Displayzeiten" v={s.display_hours} on={upd("display_hours")} />
        <div className="md:col-span-2">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Datenschutztext</label>
          <textarea rows={3} value={s.privacy_text || ""} onChange={(e) => upd("privacy_text")(e.target.value)} className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
      </div>

      {/* Standard-Ticketpreise */}
      <div className="card-lux p-6" data-testid="settings-prices">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Ticket-Preise</div>
        <div className="font-heading text-2xl mb-4">Standard-Preisliste</div>
        <div className="grid sm:grid-cols-3 gap-4">
          <PriceField label="Standard-Ticketpreis (€)" k="ticket_price" step={0.5} value={s.ticket_price} onChange={upd("ticket_price")} />
          <PriceField label="Kind (Multiplikator)" k="ticket_price_child_mult" step={0.05} value={s.ticket_price_child_mult} onChange={upd("ticket_price_child_mult")} />
          <PriceField label="Senior (Multiplikator)" k="ticket_price_senior_mult" step={0.05} value={s.ticket_price_senior_mult} onChange={upd("ticket_price_senior_mult")} />
        </div>
        <div className="text-[10px] text-[#575148] mt-3">
          Der Standardpreis wird beim Anlegen neuer Vorstellungen vorbelegt. Aufpreise für VIP/Sofa/Loveseat kommen aus dem <span className="text-[#D4AF37]">Sitzplan-Editor</span> pro Sitztyp (Feld &bdquo;Aufpreis&ldquo;) und werden pro Sitz automatisch aufaddiert. Rollstuhl-Plätze sind mit override_price 0 € standardmäßig kostenlos.
        </div>
      </div>

      {/* Familientarif */}
      <div className="card-lux p-6" data-testid="settings-family">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-1">Familientarif · Auto-Rabatt</div>
        <div className="font-heading text-2xl mb-4">Gruppen-Rabatt</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <PriceField label="Ab wie vielen Tickets" k="family_discount_min_tickets" step={1} value={s.family_discount_min_tickets ?? 5} onChange={upd("family_discount_min_tickets")} />
          <PriceField label="Rabatt (%)" k="family_discount_percent" step={1} value={s.family_discount_percent ?? 10} onChange={upd("family_discount_percent")} />
        </div>
        <div className="text-[10px] text-[#575148] mt-3">
          Wird automatisch abgezogen — sowohl bei Online-Buchung als auch an der Kasse. „0" deaktiviert den Rabatt.
        </div>
      </div>

      {/* Dynamic Pricing */}
      <DynamicPricingCard s={s} upd={upd} />

      {/* Drucker-Übersicht (Discovery + Status) */}
      <PrinterOverview />

      {/* Stripe-Konfiguration */}
      <StripeSettings />

      {/* Bondrucker &amp; Kassenlade */}
      <div className="card-lux p-6 space-y-4" data-testid="settings-printer">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Kassen-Hardware</div>
          <div className="font-heading text-2xl">Bondrucker &amp; Kassenlade</div>
          <div className="text-xs text-[#A8A398] mt-1">ESC/POS-Bondrucker (Epson TM, Star, generisch) — Anschluss via <strong>Netzwerk/WLAN</strong> (TCP:9100), USB oder CUPS-Warteschlange.</div>
        </div>
        <PrinterConnectionRow prefix="receipt" s={s} upd={upd} />
        <div className="grid md:grid-cols-2 gap-4 border-t border-[#1A1A1A] pt-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#575148] mb-2">Ticket-/Kartendrucker (2. ESC/POS-Gerät)</div>
            <PrinterConnectionRow prefix="card" s={s} upd={upd} />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!s.drawer_auto_open} onChange={(e) => upd("drawer_auto_open")(e.target.checked)}
            className="accent-[#D4AF37]" data-testid="drawer-auto-open" />
          <span className="text-sm text-[#F5F0E6]">Kassenlade automatisch bei Bar-Zahlung öffnen (nur Netzwerk-Anschluss)</span>
        </label>
      </div>

      {/* Etikettendrucker (TSPL2, Toshiba/DHL Yellow) */}
      <div className="card-lux p-6 space-y-4" data-testid="settings-labelprinter">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Kassen-Hardware</div>
          <div className="font-heading text-2xl">Ticket-Etikettendrucker</div>
          <div className="text-xs text-[#A8A398] mt-1">
            Für Toshiba / DHL Yellow / TSC-Thermodrucker (TSPL2 über IP:9100). Tickets werden als 100×150 mm-Etikett gedruckt mit Filmtitel, Reihe/Platz, QR-Code und Ticket-Nr.
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <F label="Drucker IP" v={s.label_printer_ip} on={upd("label_printer_ip")} testid="label-printer-ip" />
          <F label="Port" v={s.label_printer_port ?? 9100} on={(v) => upd("label_printer_port")(parseInt(v || 9100))} type="number" testid="label-printer-port" />
          <F label="Etikett Breite (mm)" v={s.label_width_mm ?? 100} on={(v) => upd("label_width_mm")(parseInt(v || 100))} type="number" testid="label-width" />
          <F label="Etikett Höhe (mm)" v={s.label_height_mm ?? 150} on={(v) => upd("label_height_mm")(parseInt(v || 150))} type="number" testid="label-height" />
        </div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!s.ticket_auto_print_label} onChange={(e) => upd("ticket_auto_print_label")(e.target.checked)}
              className="accent-[#D4AF37]" data-testid="ticket-auto-print" />
            <span className="text-sm text-[#F5F0E6]">Bei jedem Ticket-Verkauf automatisch aufs Etikett drucken</span>
          </label>
          <button onClick={async () => {
              try { const { data } = await api.post("/pos/label-test"); toast.success(`Testdruck gestartet (${data.printer})`); }
              catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Drucker nicht erreichbar"); }
            }} data-testid="label-test-btn"
            className="px-4 py-2 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10">
            Testdruck senden
          </button>
        </div>
      </div>

      {/* Bürodrucker (AirPrint/IPP · A4) */}
      <div className="card-lux p-6 space-y-4" data-testid="settings-office-printer">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Kassen-Hardware</div>
            <div className="font-heading text-2xl">Bürodrucker · A4-PDF</div>
            <div className="text-xs text-[#A8A398] mt-1">
              AirPrint/IPP-Drucker (Brother MFC-J5730DW, HP OfficeJet, Canon Pixma, Epson EcoTank, …) für A4-Belege, A4-Tickets und Wochenplakat-Ausdrucke. Ergänzung zum Thermodrucker — nicht als Ersatz.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={!!s.office_printer_enabled} onChange={(e) => upd("office_printer_enabled")(e.target.checked)}
              className="accent-[#D4AF37]" data-testid="office-printer-enabled" />
            <span className="text-sm text-[#F5F0E6]">Aktiv</span>
          </label>
        </div>
        {s.office_printer_enabled && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label="Drucker-Bezeichnung (frei)" v={s.office_printer_model} on={upd("office_printer_model")} testid="office-model" />
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anschluss</label>
                <div className="flex gap-1 mt-1 bg-[#050505] border border-[#2A2A2A] rounded p-0.5 w-fit">
                  {[
                    { v: "cups", l: "CUPS · empfohlen", i: <Printer size={12} /> },
                    { v: "ipp", l: "Direkt IPP", i: <Wifi size={12} /> },
                  ].map((o) => (
                    <button key={o.v} onClick={() => upd("office_connection")(o.v)}
                      data-testid={`office-conn-${o.v}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest rounded transitions-safe ${
                        (s.office_connection || "cups") === o.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#F5F0E6]"
                      }`}>
                      {o.i} {o.l}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {(s.office_connection || "cups") === "cups" ? (
              <div>
                <F label="CUPS-Druckername (z. B. Brother_MFC_J5730DW)" v={s.office_cups_name} on={upd("office_cups_name")} testid="office-cups-name" />
                <div className="text-[10px] text-[#575148] mt-1 italic">
                  Der Brother MFC-J5730DW erscheint nach dem Einbinden per Bonjour/Add-Printer als CUPS-Queue. Terminal: `lpstat -a` listet alle Queues. Nutze den Namen exakt (nicht die IP).
                </div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-3">
                <div className="md:col-span-2">
                  <F label="Drucker-IP (LAN/WLAN)" v={s.office_printer_ip} on={upd("office_printer_ip")} testid="office-ip" />
                  <div className="text-[10px] text-[#575148] mt-1 italic">
                    Die IP steht am Brother-Display unter &quot;Netzwerk-Info&quot; (typisch 192.168.1.x). Für AirPrint muss der Drucker im gleichen WLAN sein.
                  </div>
                </div>
                <F label="IPP-Port" v={s.office_printer_port ?? 631} on={(v) => upd("office_printer_port")(parseInt(v || 631))} type="number" testid="office-port" />
              </div>
            )}
            <div className="border-t border-[#1A1A1A] pt-3 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!s.office_auto_print_receipt} onChange={(e) => upd("office_auto_print_receipt")(e.target.checked)}
                  className="accent-[#D4AF37]" data-testid="office-auto-receipt" />
                <span className="text-sm text-[#F5F0E6]">Auto-Druck bei jedem POS-Verkauf: <b className="text-[#D4AF37]">A4-Beleg</b> (Kunden-Doppel)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!s.office_auto_print_receipt_thermal} onChange={(e) => upd("office_auto_print_receipt_thermal")(e.target.checked)}
                  className="accent-[#D4AF37]" data-testid="office-auto-receipt-thermal" />
                <span className="text-sm text-[#F5F0E6]">Zusätzlich <b className="text-[#D4AF37]">Thermobon 80&nbsp;mm</b> automatisch drucken</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!s.office_auto_print_invoice} onChange={(e) => upd("office_auto_print_invoice")(e.target.checked)}
                  className="accent-[#D4AF37]" data-testid="office-auto-invoice" />
                <span className="text-sm text-[#F5F0E6]">Zusätzlich <b className="text-[#D4AF37]">Rechnung A4</b> automatisch drucken</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!s.office_auto_print_ticket} onChange={(e) => upd("office_auto_print_ticket")(e.target.checked)}
                  className="accent-[#D4AF37]" data-testid="office-auto-ticket" />
                <span className="text-sm text-[#F5F0E6]">Zusätzlich <b className="text-[#D4AF37]">A4-Ticket-PDF</b> drucken (Fallback ohne Thermodrucker)</span>
              </label>
              <div className="text-[10px] text-[#575148] italic pt-2 pl-6">
                Alle Auto-Drucke nutzen deine Vorlagen aus dem <b className="text-[#EEDC82] not-italic">Dokumenten-Designer</b> — jede Layout-Änderung wirkt sofort auf den nächsten Druck.
              </div>
            </div>
          </>
        )}
      </div>

      {/* Backup */}
      <div className="card-lux p-6" data-testid="settings-backup">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Datensicherung</div>
        <div className="font-heading text-2xl">Backup &amp; Wiederherstellung</div>
        <p className="text-xs text-[#A8A398] mt-1 mb-3">Kompletter DB-Snapshot als JSON — inkl. Filme, Vorstellungen, Tickets, Bestellungen, Gutscheine, Kunden, Einstellungen. Regelmäßig herunterladen und sicher lagern.</p>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/backup`} download data-testid="backup-download"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black px-6 py-3 rounded text-[11px] uppercase tracking-widest hover:bg-[#EEDC82]">
          Backup jetzt herunterladen
        </a>
      </div>

      {/* Landing-Page Inhalte */}
      <div className="card-lux p-6 space-y-6" data-testid="settings-landing">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Startseite</div>
          <div className="font-heading text-2xl">Inhalte der Landing-Page</div>
          <div className="text-xs text-[#A8A398] mt-1">Aktions-Banner · Gäste-Stimmen · FAQ · Gutschein-Beträge</div>
        </div>

        {/* Promo-Banner */}
        <div className="border border-[#2A2A2A] rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">Aktions-Banner</div>
            <label className="flex items-center gap-2 text-xs text-[#A8A398] cursor-pointer">
              <input type="checkbox" checked={!!promo.active} onChange={(e) => updPromo("active")(e.target.checked)}
                className="accent-[#D4AF37]" data-testid="promo-active" />
              Sichtbar
            </label>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Überschrift" v={promo.title} on={updPromo("title")} testid="promo-title" />
            <F label="Text" v={promo.text} on={updPromo("text")} testid="promo-text" />
            <F label="Button-Beschriftung" v={promo.cta} on={updPromo("cta")} testid="promo-cta" />
            <F label="Button-Link" v={promo.cta_href} on={updPromo("cta_href")} testid="promo-cta-href" />
          </div>
        </div>

        {/* Testimonials */}
        <div className="border border-[#2A2A2A] rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">Gäste-Stimmen (Karussell)</div>
            <button onClick={addTest} data-testid="add-testimonial"
              className="text-xs px-3 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] flex items-center gap-1">
              <Plus size={12} /> Zitat
            </button>
          </div>
          <div className="space-y-3">
            {testimonials.map((t, i) => (
              <div key={i} className="flex gap-2" data-testid={`testimonial-row-${i}`}>
                <input value={t.author || ""} onChange={(e) => updTest(i, "author", e.target.value)} placeholder="Name (z. B. Anna & Ben)"
                  className="w-40 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                <input value={t.text || ""} onChange={(e) => updTest(i, "text", e.target.value)} placeholder="Was hat der Gast gesagt?"
                  className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                <button onClick={() => removeTest(i)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]" aria-label="Entfernen">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            {testimonials.length === 0 && <div className="text-xs text-[#575148]">Noch keine Zitate — Landing-Page nutzt Default-Beispiele.</div>}
          </div>
        </div>

        {/* FAQ */}
        <div className="border border-[#2A2A2A] rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37]">FAQ / Hausregeln</div>
            <button onClick={addFaq} data-testid="add-faq"
              className="text-xs px-3 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37] flex items-center gap-1">
              <Plus size={12} /> Frage
            </button>
          </div>
          <div className="space-y-3">
            {faq.map((f, i) => (
              <div key={i} className="border border-[#1A1A1A] rounded p-3" data-testid={`faq-row-${i}`}>
                <div className="flex gap-2">
                  <input value={f.q || ""} onChange={(e) => updFaq(i, "q", e.target.value)} placeholder="Frage"
                    className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                  <button onClick={() => removeFaq(i)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]">
                    <Trash2 size={14} />
                  </button>
                </div>
                <textarea value={f.a || ""} onChange={(e) => updFaq(i, "a", e.target.value)} rows={2} placeholder="Antwort"
                  className="w-full mt-2 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
              </div>
            ))}
            {faq.length === 0 && <div className="text-xs text-[#575148]">Noch keine FAQs — Landing-Page nutzt Default-Fragen.</div>}
          </div>
        </div>

        {/* Voucher amounts */}
        <div className="border border-[#2A2A2A] rounded-lg p-4">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2">Gutschein-Beträge</div>
          <div className="text-xs text-[#A8A398] mb-2">Kommagetrennt, z. B. &quot;15, 25, 50, 100&quot;</div>
          <input value={voucherAmounts} data-testid="voucher-amounts"
            onChange={(e) => setS((x) => ({
              ...x,
              voucher_amounts: e.target.value.split(",").map((v) => parseInt(v.trim())).filter((v) => v > 0 && v <= 500),
            }))}
            className="w-full bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
      </div>

      <div className="card-lux p-6">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] mb-4">Movie House Club · Tier-Belohnungen</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {CLUB_TIERS.map((t) => (
            <div key={t.v} className="border border-[#2A2A2A] rounded-lg p-4" style={{ borderTop: `3px solid ${t.color}` }}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                <div>
                  <div className="font-heading text-xl" style={{ color: t.color }}>{t.l}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A8A398]">ab {t.min} Besuchen</div>
                </div>
                <button onClick={() => addBenefit(t.v)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#D4AF37]">+ Vorteil</button>
              </div>
              <div className="space-y-2">
                {(s.tier_benefits?.[t.v] || []).map((b, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={b} onChange={(e) => updBenefit(t.v, i, e.target.value)}
                      className="flex-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                    <button onClick={() => removeBenefit(t.v, i)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]">✕</button>
                  </div>
                ))}
                {!s.tier_benefits?.[t.v]?.length && <div className="text-xs text-[#A8A398]">Keine Vorteile.</div>}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6">
          <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Trailer-Werbeblock: Anzahl Trailer vor dem Hauptfilm</label>
          <input type="number" min="0" max="5" value={s.trailer_preroll_count ?? 2}
            onChange={(e) => upd("trailer_preroll_count")(parseInt(e.target.value || 0))}
            className="w-24 mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
        </div>
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-widest text-[#A8A398] mb-2">Snackbar-Rabatt pro Tier</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {CLUB_TIERS.map((t) => (
              <div key={t.v} className="border border-[#2A2A2A] rounded p-3" style={{ borderTop: `2px solid ${t.color}` }}>
                <div className="text-xs uppercase tracking-widest" style={{ color: t.color }}>{t.l}</div>
                <div className="mt-2 flex items-center gap-2">
                  <input type="number" step="0.01" min="0" max="1"
                    value={s.tier_discounts?.[t.v] ?? 0}
                    onChange={(e) => setS((x) => ({ ...x, tier_discounts: { ...(x.tier_discounts||{}), [t.v]: parseFloat(e.target.value || 0) } }))}
                    className="w-24 bg-[#050505] border border-[#2A2A2A] rounded px-2 py-1 text-sm outline-none focus:border-[#D4AF37]" />
                  <span className="text-xs text-[#A8A398]">= {Math.round((s.tier_discounts?.[t.v] ?? 0) * 100)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <button data-testid="settings-save-btn" onClick={save} className="bg-[#D4AF37] text-black px-6 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82]">Speichern</button>
    </div>
  );
}
function F({ label, v, on, type = "text", testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 outline-none focus:border-[#D4AF37]" />
    </div>
  );
}

/** Konfiguration eines einzelnen ESC/POS-Druckers (Netzwerk / USB / CUPS). */
function PrinterConnectionRow({ prefix, s, upd }) {
  const conn = s[`${prefix}_connection`] || "network";
  return (
    <div className="space-y-3" data-testid={`printer-config-${prefix}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">Anschluss</label>
        <div className="flex gap-1 bg-[#050505] border border-[#2A2A2A] rounded p-0.5">
          {[
            { v: "network", l: "Netzwerk / WLAN", i: <Wifi size={12} /> },
            { v: "usb", l: "USB", i: <Usb size={12} /> },
            { v: "cups", l: "CUPS", i: <Printer size={12} /> },
          ].map((o) => (
            <button key={o.v} onClick={() => upd(`${prefix}_connection`)(o.v)}
              data-testid={`printer-conn-${prefix}-${o.v}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest rounded transitions-safe ${
                conn === o.v ? "bg-[#D4AF37] text-black" : "text-[#A8A398] hover:text-[#F5F0E6]"
              }`}>
              {o.i} {o.l}
            </button>
          ))}
        </div>
      </div>
      {conn === "network" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <F label="Drucker IP" v={s[`${prefix}_printer_ip`]} on={upd(`${prefix}_printer_ip`)} testid={`printer-ip-${prefix}`} />
          <F label="Port" v={s[`${prefix}_printer_port`] ?? 9100} on={(v) => upd(`${prefix}_printer_port`)(parseInt(v || 9100))} type="number" testid={`printer-port-${prefix}`} />
        </div>
      )}
      {conn === "usb" && (
        <div>
          <F label="USB-Gerät (z. B. /dev/usb/lp0)" v={s[`${prefix}_usb_device`]} on={upd(`${prefix}_usb_device`)} testid={`printer-usb-${prefix}`} />
          <div className="text-[10px] text-[#575148] mt-1 italic">
            Linux: `ls /dev/usb/lp*` zeigt angeschlossene ESC/POS-Drucker. Rechte: `sudo usermod -aG lp $USER` und neu einloggen. Nutze das <b>Drucker-Übersicht</b>-Panel oben für automatische Erkennung.
          </div>
        </div>
      )}
      {conn === "cups" && (
        <div>
          <F label="CUPS-Druckername" v={s[`${prefix}_cups_name`]} on={upd(`${prefix}_cups_name`)} testid={`printer-cups-${prefix}`} />
          <div className="text-[10px] text-[#575148] mt-1 italic">
            CUPS muss installiert sein: `lpstat -a` zeigt verfügbare Queues. Nutze Druckernamen (nicht URI). Payload wird via `lp -d NAME -o raw` gesendet.
          </div>
        </div>
      )}
    </div>
  );
}

/** Stripe-Konfiguration: Modus, maskierte Keys, Verbindungstest. */
function StripeSettings() {
  const [cfg, setCfg] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);
  const load = () => api.get("/stripe/settings").then((r) => setCfg(r.data)).catch(() => setCfg(null));
  React.useEffect(() => { load(); }, []);
  const test = async () => {
    setBusy(true); setTestResult(null);
    try {
      const { data } = await api.post("/stripe/test");
      setTestResult({ ok: true, ...data });
      toast.success(data.livemode ? "Live-Modus verbunden" : "Test-Modus verbunden");
    } catch (e) {
      setTestResult({ ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || "Fehler" });
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Stripe-Test fehlgeschlagen");
    } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <div className="card-lux p-6 space-y-4" data-testid="stripe-settings">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#635BFF] flex items-center gap-1.5"><CreditCard size={11} /> Online-Zahlung</div>
          <div className="font-heading text-2xl">Stripe-Konfiguration</div>
          <div className="text-xs text-[#A8A398] mt-1">Zahlungsprozessor für Shop (Voucher/Pack), Kiosk-Tickets und POS-Kartenzahlung.</div>
        </div>
        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-[10px] uppercase tracking-widest ${cfg.configured ? "border border-[#8FBC8F]/60 text-[#B7E1B7] bg-[#8FBC8F]/10" : "border border-[#EE9CA1]/60 text-[#EE9CA1] bg-[#7A1921]/10"}`}>
          {cfg.configured ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />}
          {cfg.mode?.toUpperCase()} · {cfg.configured ? "verbunden" : "nicht konfiguriert"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <KVField label="Secret Key" value={cfg.secret_key_masked} help="STRIPE_SECRET_KEY aus .env — nur lesend." mono />
        <KVField label="Publishable Key" value={cfg.publishable_key_masked || cfg.publishable_key || "—"} help="Kann öffentlich sichtbar sein." mono />
        <KVField label="Webhook Secret" value={cfg.webhook_secret_configured ? cfg.webhook_secret_masked : "— nicht gesetzt —"} help="Für Webhook-Signaturprüfung. Ohne Webhook funktioniert nur Polling." mono />
        <KVField label="Modus" value={cfg.mode} help="test für Testkarten, live für Echtgeld." />
      </div>

      <div className="flex items-center gap-3 pt-3 border-t border-[#1A1A1A]">
        <button onClick={test} disabled={busy || !cfg.configured} data-testid="stripe-test-btn"
          className="inline-flex items-center gap-2 px-4 py-2 border border-[#635BFF]/60 text-[#9C97FF] rounded text-[11px] uppercase tracking-widest hover:bg-[#635BFF] hover:text-white disabled:opacity-40">
          <Zap size={12} /> {busy ? "Prüfe …" : "Verbindung testen"}
        </button>
        <div className="text-[10px] text-[#575148] italic flex-1">
          Ruft stripe.Balance.retrieve() auf — verifiziert API-Key + Netzwerk.
        </div>
      </div>

      {testResult && (
        <div className={`text-xs p-3 rounded border ${testResult.ok ? "border-[#8FBC8F]/40 bg-[#8FBC8F]/5 text-[#B7E1B7]" : "border-[#EE9CA1]/40 bg-[#7A1921]/10 text-[#EE9CA1]"}`} data-testid="stripe-test-result">
          {testResult.ok ? (
            <>
              <div className="font-mono">✓ {testResult.livemode ? "LIVE-Modus" : "TEST-Modus"}</div>
              {testResult.available?.length > 0 && (
                <div className="mt-1">Verfügbar: {testResult.available.map((a) => `${a.amount.toFixed(2)} ${a.currency.toUpperCase()}`).join(", ")}</div>
              )}
              {testResult.pending?.length > 0 && (
                <div className="mt-1">Pending: {testResult.pending.map((a) => `${a.amount.toFixed(2)} ${a.currency.toUpperCase()}`).join(", ")}</div>
              )}
            </>
          ) : (
            <div>× {testResult.error}</div>
          )}
        </div>
      )}

      <div className="text-[10px] text-[#575148] italic">
        Konfiguration ändern? Passe die ENV-Variablen STRIPE_SECRET_KEY, STRIPE_PUBLISHABLE_KEY, STRIPE_WEBHOOK_SECRET, STRIPE_MODE in <span className="font-mono">/app/backend/.env</span> an und starte den Backend-Service neu.
      </div>
    </div>
  );
}

function KVField({ label, value, help, mono }) {
  return (
    <div className="p-3 bg-[#050505] border border-[#1A1A1A] rounded">
      <div className="text-[9px] uppercase tracking-widest text-[#575148]">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : ""} text-[#F5F0E6] truncate`}>{value || "—"}</div>
      {help && <div className="text-[10px] text-[#575148] mt-1 italic">{help}</div>}
    </div>
  );
}


/** Übersicht aller Drucker: Auto-Discovery + Test-Druck pro Gerät. */
function PrinterOverview() {
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [testingKey, setTestingKey] = React.useState("");
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/printers/discover");
      setData(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Discovery fehlgeschlagen"); }
    finally { setBusy(false); }
  };
  React.useEffect(() => { load(); }, []);
  const testPrint = async (key) => {
    setTestingKey(key);
    try {
      await api.post(`/admin/printers/test/${key}`);
      toast.success(`Testdruck an ${key} gestartet`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Testdruck fehlgeschlagen"); }
    finally { setTestingKey(""); load(); }
  };
  const iconFor = (icn) => {
    const p = { size: 22, className: "text-[#D4AF37]" };
    if (icn === "credit-card")  return <CreditCard {...p} />;
    if (icn === "tag")          return <Tag {...p} />;
    if (icn === "receipt")      return <Receipt {...p} />;
    if (icn === "printer-a4")   return <Printer {...p} />;
    return <Printer {...p} />;
  };
  return (
    <div className="card-lux p-6 space-y-4" data-testid="printer-overview">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37]">Kassen-Hardware</div>
          <div className="font-heading text-2xl">Drucker-Übersicht</div>
          <div className="text-xs text-[#A8A398] mt-1">Auto-Discovery aller Bon-, Ticket- und Etikettendrucker im System. USB-Geräte, CUPS-Warteschlangen und Netzwerkdrucker.</div>
        </div>
        <button onClick={load} disabled={busy} data-testid="printer-overview-refresh"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/50 text-[#D4AF37] rounded text-[11px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-40">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Neu scannen
        </button>
      </div>

      {/* Available Resources */}
      {data?.available && (
        <div className="grid md:grid-cols-2 gap-3 pb-3 border-b border-[#1A1A1A]">
          <div className="p-3 bg-[#050505] border border-[#2A2A2A] rounded" data-testid="printer-usb-list">
            <div className="text-[10px] uppercase tracking-widest text-[#575148] flex items-center gap-1"><Usb size={11} /> USB-Geräte auf Host ({data.available.host_os || ""})</div>
            {(data.available.usb_devices || []).length === 0 ? (
              <div className="text-xs text-[#575148] italic mt-2">Keine USB-Drucker gefunden. Prüfe: Kabel · Netzstrom · `usblp`-Modul (`sudo modprobe usblp`) · Rechte.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.available.usb_devices.map((d) => (
                  <span key={d} className="font-mono text-xs px-2 py-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#EEDC82] rounded">{d}</span>
                ))}
              </div>
            )}
          </div>
          <div className="p-3 bg-[#050505] border border-[#2A2A2A] rounded" data-testid="printer-cups-list">
            <div className="text-[10px] uppercase tracking-widest text-[#575148] flex items-center gap-1"><Printer size={11} /> CUPS-Warteschlangen</div>
            {(data.available.cups_printers || []).length === 0 ? (
              <div className="text-xs text-[#575148] italic mt-2">Keine CUPS-Queues konfiguriert. Öffne `system-config-printer` oder `cupsd` und richte Drucker ein.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {data.available.cups_printers.map((n) => (
                  <span key={n} className="font-mono text-xs px-2 py-1 bg-[#8FBC8F]/10 border border-[#8FBC8F]/30 text-[#B7E1B7] rounded">{n}</span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Configured printers */}
      <div className="space-y-2" data-testid="printer-list">
        {(data?.printers || []).map((p) => (
          <div key={p.key} className="flex items-center gap-4 p-3 bg-[#050505] border border-[#1A1A1A] rounded" data-testid={`printer-row-${p.key}`}>
            {iconFor(p.icon)}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-[#F5F0E6]">{p.name}</div>
              <div className="text-xs text-[#575148] flex items-center gap-2 flex-wrap mt-0.5">
                <span className="inline-flex items-center gap-1 uppercase tracking-widest text-[10px] px-1.5 py-0.5 border border-[#2A2A2A] rounded">
                  {p.connection === "usb" ? <Usb size={9} /> : p.connection === "cups" ? <Printer size={9} /> : <Wifi size={9} />}
                  {p.connection}
                </span>
                {p.configured ? (
                  <span className="font-mono truncate">{p.target}{p.port ? `:${p.port}` : ""}</span>
                ) : (
                  <span className="italic">— nicht konfiguriert —</span>
                )}
              </div>
              {p.error && <div className="text-[10px] text-[#EE9CA1] mt-1">{p.error}</div>}
            </div>
            {p.configured ? (
              p.reachable ? (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8FBC8F]" data-testid={`printer-status-${p.key}-ok`}>
                  <ShieldCheck size={11} /> erreichbar
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#EE9CA1]" data-testid={`printer-status-${p.key}-fail`}>
                  <ShieldAlert size={11} /> nicht erreichbar
                </span>
              )
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#575148]">unkonfiguriert</span>
            )}
            <button onClick={() => testPrint(p.key)} disabled={!p.configured || testingKey === p.key}
              data-testid={`printer-test-${p.key}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[#D4AF37]/40 text-[#D4AF37] rounded text-[10px] uppercase tracking-widest hover:bg-[#D4AF37]/10 disabled:opacity-30 disabled:cursor-not-allowed">
              <PlayCircle size={11} /> {testingKey === p.key ? "…" : "Testdruck"}
            </button>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-[#575148] italic">
        Konfiguration je Drucker weiter unten. Nach Änderungen &quot;Speichern&quot; klicken → dann &quot;Neu scannen&quot; für aktualisierten Status.
      </div>
    </div>
  );
}
